import { isNil } from 'ramda';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IApiToken } from '../../../../common/models/workspace/IApiToken';
import Input from '../../../../components/Input';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import ModalWindow from '../../../../components/ModalWindow';
import RegularButton from '../../../../components/RegularButton';
import { copyToClipboard } from '../../../../common/helpers/copyHelper';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import {
  createApiTokenRoutine,
  reactivateApiTokenRoutine,
  deactivateApiTokenRoutine,
  deleteApiTokenRoutine,
  getApiTokensRoutine,
  resetApiTokenRoutine
} from '../../routines';
import styles from './styles.module.scss';
import ConfirmationModal from '../../../../components/ConfirmationModal';

interface IProps {
  tokens?: IApiToken[];
  apiToken?: string;
  isSaving: boolean;
  createToken: IBindingCallback1<string>;
  deactivateToken: IBindingCallback1<string>;
  reactivateToken: IBindingCallback1<string>;
  deleteToken: IBindingCallback1<string>;
  getTokens: IBindingAction;
  resetApiToken: IBindingAction;
}

const ApiTokensPane: FunctionComponent<IProps> = ({
  tokens,
  apiToken,
  isSaving,
  getTokens,
  deleteToken,
  createToken,
  resetApiToken,
  deactivateToken,
  reactivateToken
}) => {
  const [tokenName, setTokenName] = useState<undefined | string>();
  const [deletedName, setDeletedName] = useState<undefined | string>();

  useEffect(() => {
    if (!tokens) {
      getTokens();
    }
  }, []);

  const copyToken = () => copyToClipboard(apiToken as string, {
    id: 'tokenCopied',
    defaultMessage: 'Api token was successfully copied to clipboard'
  });

  const closeApiTokenModal = () => {
    resetApiToken();
    setTokenName(undefined);
  };

  const onDelete = () => {
    deleteToken(deletedName as string);
    setDeletedName(undefined);
  };

  return (
    <div className="position-relative h-100 d-flex flex-column">
      <LoaderWrapper transparent={Boolean(tokens)} loading={isSaving && !tokenName}>
        <header className="d-flex justify-content-between align-items-center">
          <div className="settingsTitle mb-0">
            <FormattedMessage id="integrationsPane.title" defaultMessage="KodiakHR API Tokens" />
          </div>
          <RegularButton
            textIntlId="integrationsPane.create"
            defaultMessage="Create API token"
            onClick={() => setTokenName('')}
          />
        </header>
        {Boolean(tokens?.length) && (
          <Table
            className={styles.tokensTable}
            config={{
              data: { items: tokens as IApiToken[], total: (tokens as IApiToken[]).length },
              columns: getColumns({ deactivateToken, reactivateToken, deleteToken: setDeletedName }),
              hideHeader: true
            }}
          />
        )}
        <ConfirmationModal
          isShown={Boolean(deletedName)}
          titleText={{ id: 'deleteIntegration.title', defaultMessage: 'Delete API token?' }}
          descriptionText={{
            id: 'deleteIntegration.description',
            defaultMessage: `This will revoke access for all apps that use this token. 
            Are you sure you want to delete it?`
          }}
          confirmBttnText={{ id: 'delete', defaultMessage: 'Delete' }}
          cancelBttnText={{ id: 'goBack', defaultMessage: 'No, go back' }}
          onCancel={() => setDeletedName(undefined)}
          onConfirm={onDelete}
        />
        <ModalWindow
          isShown={!isNil(tokenName)}
          onHide={() => setTokenName(undefined)}
          modalClassName="modal-wrapper"
          contentClassName={styles.tokenModalContent}
        >
          <div className={styles.tokenModal}>
            <LoaderWrapper loading={isSaving} transparent>
              <header className="custom-modal-header">
                <FormattedMessage id="integrationsPane.create" defaultMessage="Create API token" />
              </header>
              <main className={styles.tokenModalBody}>
                {apiToken
                  ? (
                    <>
                      <div onClick={copyToken} role="presentation" className={styles.copyTokenContainer}>
                        {apiToken}
                      </div>
                      <span>
                        <FormattedMessage
                          id="integrationsPane.warning"
                          defaultMessage={`Please click to copy this token and save it somewhere safe. 
                          For security reasons, we cannot show it to you again.`}
                        />
                      </span>
                    </>
                  )
                  : (
                    <Input
                      value={tokenName}
                      onChange={setTokenName}
                      variant="underlined"
                      placeholder={{ id: 'tokenNamePlaceholder', defaultMessage: 'Type the name of your ATS here' }}
                    />
                  )}
              </main>
              <footer className="modal-footer-bttns">
                {apiToken
                  ? (
                    <RegularButton
                      textIntlId="done"
                      defaultMessage="Done"
                      onClick={closeApiTokenModal}
                    />
                  )
                  : (
                    <RegularButton
                      textIntlId="save"
                      defaultMessage="Save"
                      disabled={!tokenName?.trim()}
                      onClick={() => createToken((tokenName as string)?.trim())}
                    />
                  )}
              </footer>
            </LoaderWrapper>
          </div>
        </ModalWindow>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  settings: { isSaving, apiTokens: { tokens, apiToken } }
}: IAppState) => ({ apiToken, tokens, isSaving });

const mapDispachToProps = {
  createToken: createApiTokenRoutine,
  deactivateToken: deactivateApiTokenRoutine,
  reactivateToken: reactivateApiTokenRoutine,
  deleteToken: deleteApiTokenRoutine,
  getTokens: getApiTokensRoutine,
  resetApiToken: resetApiTokenRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ApiTokensPane);
